import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getDashboardPath } from '../components/routing/DashboardRouter';

interface LocationState {
  from?: {
    pathname: string;
  };
}

export function LoginPage() {
  const { isAuthenticated, user, login, requestOTP, error, isLoading, clearError } = useAuth();
  const location = useLocation();
  const state = location.state as LocationState;
  
  const [step, setStep] = useState<'request' | 'verify'>('request');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    mobile: '',
    otp: '',
  });

  // Redirect if already authenticated
  if (isAuthenticated && user) {
    const from = state?.from?.pathname || getDashboardPath(user.role);
    return <Navigate to={from} replace />;
  }

  const handleRequestOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    try {
      await requestOTP(formData.fullName, formData.email, formData.mobile);
      setStep('verify');
    } catch (error) {
      // Error is handled by the auth context
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    try {
      await login(formData.email, formData.otp);
      // Navigation will happen automatically via the redirect above
    } catch (error) {
      // Error is handled by the auth context
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBackToRequest = () => {
    setStep('request');
    setFormData(prev => ({ ...prev, otp: '' }));
    clearError();
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        maxWidth: '500px',
        width: '100%',
        background: 'white',
        borderRadius: '20px',
        padding: '40px',
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h1 style={{
            fontSize: '2.5em',
            marginBottom: '10px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 'bold'
          }}>
            🎯 CBM Prototype
          </h1>
          <p style={{ fontSize: '1.1em', color: '#666' }}>
            {step === 'request' 
              ? 'Secure Access Required' 
              : 'Enter Verification Code'
            }
          </p>
        </div>

        {error && (
          <div style={{
            background: '#fee',
            border: '2px solid #fcc',
            borderRadius: '10px',
            padding: '15px',
            marginBottom: '20px',
            color: '#c33'
          }}>
            <strong>❌ Error:</strong> {error}
          </div>
        )}

        {step === 'request' ? (
          <form onSubmit={handleRequestOTP}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontWeight: '600',
                marginBottom: '8px',
                color: '#333'
              }}>
                Full Name:
              </label>
              <input
                name="fullName"
                type="text"
                required
                value={formData.fullName}
                onChange={handleInputChange}
                placeholder="Enter your full name"
                style={{
                  width: '100%',
                  padding: '15px',
                  border: '2px solid #e1e5e9',
                  borderRadius: '10px',
                  fontSize: '16px',
                  transition: 'border-color 0.3s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
              />
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontWeight: '600',
                marginBottom: '8px',
                color: '#333'
              }}>
                Email Address:
              </label>
              <input
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email address"
                style={{
                  width: '100%',
                  padding: '15px',
                  border: '2px solid #e1e5e9',
                  borderRadius: '10px',
                  fontSize: '16px',
                  transition: 'border-color 0.3s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
              />
            </div>
            
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                fontWeight: '600',
                marginBottom: '8px',
                color: '#333'
              }}>
                Mobile Number:
              </label>
              <input
                name="mobile"
                type="tel"
                required
                value={formData.mobile}
                onChange={handleInputChange}
                placeholder="Enter your mobile number"
                style={{
                  width: '100%',
                  padding: '15px',
                  border: '2px solid #e1e5e9',
                  borderRadius: '10px',
                  fontSize: '16px',
                  transition: 'border-color 0.3s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                padding: '18px',
                fontSize: '18px',
                fontWeight: '600',
                borderRadius: '50px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.6 : 1,
                transition: 'transform 0.3s, box-shadow 0.3s'
              }}
              onMouseEnter={(e) => !isLoading && (e.currentTarget.style.transform = 'translateY(-2px)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
            >
              {isLoading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{
                    display: 'inline-block',
                    width: '16px',
                    height: '16px',
                    border: '3px solid rgba(255,255,255,0.3)',
                    borderTop: '3px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginRight: '10px'
                  }}></span>
                  Sending OTP...
                </span>
              ) : (
                'Send OTP'
              )}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOTP}>
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                fontWeight: '600',
                marginBottom: '8px',
                color: '#333'
              }}>
                Enter OTP:
              </label>
              <input
                name="otp"
                type="text"
                required
                maxLength={6}
                value={formData.otp}
                onChange={handleInputChange}
                placeholder="Enter 6-digit OTP"
                style={{
                  width: '100%',
                  padding: '15px',
                  border: '2px solid #e1e5e9',
                  borderRadius: '10px',
                  fontSize: '24px',
                  textAlign: 'center',
                  letterSpacing: '8px',
                  transition: 'border-color 0.3s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
              />
              <p style={{ marginTop: '10px', fontSize: '14px', color: '#666', textAlign: 'center' }}>
                Code sent to {formData.email}
              </p>
            </div>

            <button
              type="submit"
              disabled={isLoading || formData.otp.length !== 6}
              style={{
                width: '100%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                padding: '18px',
                fontSize: '18px',
                fontWeight: '600',
                borderRadius: '50px',
                cursor: (isLoading || formData.otp.length !== 6) ? 'not-allowed' : 'pointer',
                opacity: (isLoading || formData.otp.length !== 6) ? 0.6 : 1,
                transition: 'transform 0.3s, box-shadow 0.3s',
                marginBottom: '10px'
              }}
              onMouseEnter={(e) => !(isLoading || formData.otp.length !== 6) && (e.currentTarget.style.transform = 'translateY(-2px)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
            >
              {isLoading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{
                    display: 'inline-block',
                    width: '16px',
                    height: '16px',
                    border: '3px solid rgba(255,255,255,0.3)',
                    borderTop: '3px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginRight: '10px'
                  }}></span>
                  Verifying...
                </span>
              ) : (
                'Verify OTP'
              )}
            </button>
            
            <button
              type="button"
              onClick={handleBackToRequest}
              style={{
                width: '100%',
                background: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '12px',
                fontSize: '16px',
                fontWeight: '600',
                borderRadius: '50px',
                cursor: 'pointer',
                transition: 'background 0.3s'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#5a6268')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '#6c757d')}
            >
              Back to Login
            </button>
          </form>
        )}

        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <p style={{ fontSize: '12px', color: '#999' }}>
            🔒 Secure OTP authentication via email
          </p>
        </div>
      </div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}